'use client';

import { parseAsArrayOf, parseAsString, ParserBuilder, useQueryState, useQueryStates, Values } from 'nuqs';
import { createContext, PropsWithChildren, useCallback, useContext } from 'react';
import { useNoListingMessageStore } from '@/features/listing/hooks/use-no-listing-message-store';
export type ListingSearchParams = Values<{
  styles: ParserBuilder<string[]>;
  colors: ParserBuilder<string[]>;
  conditions: ParserBuilder<string[]>;
  occasions: ParserBuilder<string[]>;
  chest: ParserBuilder<string[]>;
  waist: ParserBuilder<string[]>;
}>;
interface SearchListingContextProps {
  params: ListingSearchParams;
  category: string | null;
  updateCategory: (value: string | null) => Promise<void>;
  updateFilter: (paramString: keyof SearchListingContextProps['params']) => (value: string, checked: boolean) => Promise<void>;
}
const SearchListingContext = createContext<SearchListingContextProps | undefined>(undefined);
export const SearchListingProvider = ({
  children
}: PropsWithChildren) => {
  const [category, setCategory] = useQueryState('category', parseAsString);
  const [params, setParams] = useQueryStates({
    styles: parseAsArrayOf(parseAsString),
    colors: parseAsArrayOf(parseAsString),
    conditions: parseAsArrayOf(parseAsString),
    occasions: parseAsArrayOf(parseAsString),
    chest: parseAsArrayOf(parseAsString),
    waist: parseAsArrayOf(parseAsString)
  });
  const setLastListingCategory = useNoListingMessageStore(store => store.setLastListingCategory);
  const updateCategory = useCallback(async (value: string | null) => {
    await setCategory(value);
    if (value) {
      setLastListingCategory(null);
    }
  }, [setCategory, setLastListingCategory]);
  const updateFilter = useCallback((paramString: keyof SearchListingContextProps['params']) => async (value: string, checked: boolean) => {
    await setParams(params => {
      const updateValues = checked ? [...(params[paramString] || []), value] : params[paramString]?.filter(param => param !== value) || [];
      return {
        ...params,
        [paramString]: updateValues
      };
    });
  }, [setParams]);
  return <SearchListingContext.Provider value={{
    params,
    updateFilter,
    category,
    updateCategory
  }} data-sentry-element="unknown" data-sentry-component="SearchListingProvider" data-sentry-source-file="search-provider.tsx">
      {children}
    </SearchListingContext.Provider>;
};
export const useSearchListing = (): SearchListingContextProps => {
  const context = useContext(SearchListingContext);
  if (!context) {
    throw new Error('useSearchListing must be used within a SearchListingProvider');
  }
  return context;
};